import moment from 'moment'
import { getRegs, insert, update } from '../../../utils/fc'

export function roundMoney(value) {
  return Number((Number(value || 0)).toFixed(2))
}

export function computeDeficit(emOrcDot, jaNoBordero, valor) {
  const saldo = roundMoney(Number(emOrcDot || 0) - Number(jaNoBordero || 0) - Number(valor || 0))
  return roundMoney(Math.max(0, -saldo))
}

export function isSameYearMonth(dateValue, yyyyMm) {
  if (!dateValue || !yyyyMm) return false
  const parsed = moment(dateValue)
  return parsed.isValid() && parsed.format('YYYY-MM') === yyyyMm
}

const MESES_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

export function labelMes(yyyyMm) {
  if (!yyyyMm) return '—'
  const parsed = moment(`${yyyyMm}-01`, 'YYYY-MM-DD')
  if (!parsed.isValid()) return yyyyMm
  return `${MESES_PT[parsed.month()]}/${parsed.year()}`
}

export function mesesSeguintesNoAno(yyyyMm) {
  if (!yyyyMm) return []
  const start = moment(`${yyyyMm}-01`, 'YYYY-MM-DD')
  if (!start.isValid()) return []
  const labels = []
  let cursor = start.clone().add(1, 'month')
  while (cursor.year() === start.year()) {
    labels.push(labelMes(cursor.format('YYYY-MM')))
    cursor.add(1, 'month')
  }
  return labels
}

function favLabel(row) {
  return row?.pf_nomeExibicao || row?.pj_razaosocial || row?.pj_nomefanta || '—'
}

const bdroFields = {
  bdro_id: 'bdro_id',
  bdro_desc: 'bdro_desc',
  bdro_vlr: 'bdro_vlr',
  bdro_dtVenc: 'bdro_dtVenc',
  bdro_dtQuit: 'bdro_dtQuit',
  bdro_orcPend: 'bdro_orcPend',
  bdro_favTab: 'bdro_favTab',
  bdro_favId: 'bdro_favId',
  orcItem_id: 'orcItem_id',
  "(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = bdro.bdro_favId AND bdro.bdro_favTab = 'pf' LIMIT 1)": 'pf_nomeExibicao',
  "(SELECT pj_razaosocial FROM pj WHERE pj.pj_id = bdro.bdro_favId AND bdro.bdro_favTab = 'pj' LIMIT 1)": 'pj_razaosocial',
}

const movFields = {
  mov_id: 'mov_id',
  mov_desc: 'mov_desc',
  mov_origMemo: 'mov_origMemo',
  mov_vlr: 'mov_vlr',
  mov_dtQuit: 'mov_dtQuit',
  bdro_id: 'bdro_id',
  orcItem_id: 'orcItem_id',
}

export async function fetchItensOrcamentoMes({ orcItemId, yyyyMm }) {
  if (!orcItemId || !yyyyMm) {
    return { linhas: [] }
  }

  const [bdroRows, movRows] = await Promise.all([
    getRegs('bdro', { orcItem_id: orcItemId }, bdroFields),
    getRegs('mov', { orcItem_id: orcItemId }, movFields),
  ])

  const bdrosMes = (bdroRows || []).filter((row) => isSameYearMonth(row.bdro_dtVenc, yyyyMm))
  const movsMes = (movRows || []).filter((row) => (
    Number(row.mov_vlr || 0) < 0 && isSameYearMonth(row.mov_dtQuit, yyyyMm)
  ))

  const bdroIdsNoMes = new Set(bdrosMes.map((row) => Number(row.bdro_id)))
  const bdroIdsComMov = new Set(
    movsMes
      .map((row) => Number(row.bdro_id))
      .filter((id) => id && bdroIdsNoMes.has(id)),
  )

  const linhasBdro = bdrosMes.map((row) => {
    const associado = bdroIdsComMov.has(Number(row.bdro_id))
    let status = 'Liberado'
    if (Number(row.bdro_orcPend) === 1) status = 'Pendente'
    else if (row.bdro_dtQuit) status = associado ? 'Pago (conciliado)' : 'Quitado'
    return {
      key: `bdro-${row.bdro_id}`,
      origem: 'Borderô',
      bdroId: Number(row.bdro_id),
      data: row.bdro_dtVenc,
      descricao: row.bdro_desc || '—',
      favorecido: favLabel(row),
      valor: Math.abs(Number(row.bdro_vlr || 0)),
      status,
      destaque: false,
    }
  })

  const linhasMov = movsMes
    .filter((row) => !row.bdro_id || !bdroIdsNoMes.has(Number(row.bdro_id)))
    .map((row) => ({
      key: `mov-${row.mov_id}`,
      origem: 'Movimento',
      bdroId: Number(row.bdro_id) || null,
      data: row.mov_dtQuit,
      descricao: row.mov_desc || row.mov_origMemo || '—',
      favorecido: '—',
      valor: Math.abs(Number(row.mov_vlr || 0)),
      status: 'Pago no caixa',
      destaque: false,
    }))

  const linhas = [...linhasBdro, ...linhasMov].sort((a, b) => String(a.data || '').localeCompare(String(b.data || '')))
  return { linhas }
}

export function podeAjustarDotacao(pfId) {
  const id = Number(pfId)
  return id === 1 || id === 2 || id === 3
}

export async function aplicarSuplementacao({
  orcItemId,
  yyyyMm,
  deficit,
  userPfId,
}) {
  if (!podeAjustarDotacao(userPfId)) {
    throw new Error('Sem permissão para ajustar a dotação orçamentária.')
  }
  const valor = roundMoney(deficit)
  if (!(valor > 0)) {
    throw new Error('Não há déficit a suplementar.')
  }
  if (!orcItemId || !yyyyMm) {
    throw new Error('Item orçamentário ou mês inválido.')
  }

  const mesLabel = moment(`${yyyyMm}-01`).format('MM/YYYY')
  const dots = await getRegs('orcDot', { orcItem_id: orcItemId }) || []
  const doMes = dots.filter((row) => isSameYearMonth(row.orcDot_data, yyyyMm))

  if (doMes.length) {
    const row = doMes[0]
    const updated = await update('orcDot', row.orcDot_id, {
      orcDot_valor: roundMoney(Number(row.orcDot_valor || 0) + valor),
      orcDot_usuarioMod: userPfId,
    })
    if (!updated) {
      throw new Error('Não foi possível atualizar a dotação mensal.')
    }
  } else {
    const created = await insert('orcDot', {
      orcItem_id: orcItemId,
      orcDot_data: `${yyyyMm}-01`,
      orcDot_valor: valor,
      orcDot_usuarioCria: userPfId,
      orcDot_usuarioMod: userPfId,
    })
    if (!created) {
      throw new Error('Não foi possível criar a dotação mensal.')
    }
  }

  const ano = Number(String(yyyyMm).slice(0, 4))
  const anuais = await getRegs('orcDotAnual', {
    orcItem_id: orcItemId,
    orcDotAnual_ano: ano,
  }) || []

  const obs = `Suplementação via borderô em ${mesLabel}`
  if (anuais.length) {
    const row = anuais[0]
    const updated = await update('orcDotAnual', row.orcDotAnual_id, {
      orcDotAnual_vlrAtu: roundMoney(Number(row.orcDotAnual_vlrAtu || 0) + valor),
      orcDotAnual_obs: row.orcDotAnual_obs ? `${row.orcDotAnual_obs}\n${obs}` : obs,
      orcDotAnual_usuarioMod: userPfId,
    })
    if (!updated) {
      throw new Error('Não foi possível atualizar a dotação anual.')
    }
  } else {
    const created = await insert('orcDotAnual', {
      orcItem_id: orcItemId,
      orcDotAnual_ano: ano,
      orcDotAnual_vlrIni: 0,
      orcDotAnual_vlrAtu: valor,
      orcDotAnual_obs: obs,
      orcDotAnual_usuarioCria: userPfId,
      orcDotAnual_usuarioMod: userPfId,
    })
    if (!created) {
      throw new Error('Não foi possível criar a dotação anual.')
    }
  }

  return valor
}
